import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ChannelGraphComponent } from '@components/channel-graph/channel-graph.component';
import { ChannelHistory, ChannelHistoryEntry } from '@models/channel-history';
import { TranslateService } from '@ngx-translate/core';
import { ChannelHistoryService } from '@service/channel-history.service';
import { ToastrService } from 'ngx-toastr';

import { PopUpNotificationComponent } from '@components/pop-up-notification/pop-up-notification.component';
import { ResponsiveService } from '@service/responsive.service';
import { TrendGraphAccessSettingsService } from '@service/trend-graph-access-settings.service';
import { saveAs } from 'file-saver';
import { CircularBuffer } from 'ops/common/circular-buffer';
import { PageAccessType } from 'ops/common/models/access-settings';
import { ChannelViewOption, TrendGraphView } from 'ops/common/models/trend-graph-view';
import { Observable, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { DescriptionDialogComponent } from '@components/description-dialog/description-dialog.component';
import { Channel, ChannelFns } from 'ops/common/models/channel';
import { AuthService } from '@service/auth.service';

@Component({
    selector: 'app-trend-graph-view',
    templateUrl: './trend-graph-view.component.html',
    styleUrls: ['./trend-graph-view.component.scss'],
    providers: [ChannelHistoryService],
    standalone: false,
})
export class TrendGraphViewComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    MIN_DATE_INDEX = 0;
    MAX_DATE_INDEX = 1999;
    MIN_GRAPH_VALUE = 0;
    MAX_GRAPH_VALUE = 100;

    DEFAULT_MIN_HEIGHT: string = '50vh';

    @ViewChild('page') page: ElementRef;
    @ViewChild('graph') graphComponent: ChannelGraphComponent;
    @ViewChild('graphElement') graphElement: ElementRef;
    @ViewChild('notification', { static: true }) notification: PopUpNotificationComponent;

    @Output() onEditView = new EventEmitter<TrendGraphView>();
    @Output() onDeleteView = new EventEmitter<string>();

    @Input() usersAccess;
    @Input() view: TrendGraphView;
    @Input() channelLookup: { [fcuChannelId: number]: Channel };

    dateRange = {
        startIndex: this.MIN_DATE_INDEX,
        endIndex: this.MAX_DATE_INDEX,
    };
    verticalViewRange = {
        start: this.MIN_GRAPH_VALUE,
        end: this.MAX_GRAPH_VALUE,
    };

    graphMinHeight = this.DEFAULT_MIN_HEIGHT;

    onDataUpdate: Observable<ChannelHistory>;
    lastDataUpdate: ChannelHistory = null;

    focusedChannel: Channel = null;
    selectedChannel: number = null;

    updateInterval: NodeJS.Timeout = null;

    isInitComplete: boolean = false;
    private isLegendVisible_: boolean = false;

    visibilityMap: { [channelNo: number]: boolean } = {};
    colors: { [fcuChannelId: number]: string } = {};

    historyMap: { [fcuChannelId: number]: ChannelHistoryEntry } = {};

    verticalSliderHeight: number = 0;
    pageAccessTypeTile: string = 'page-access.type';
    pageAccessType: PageAccessType;
    pageAccessIcon: string;
    shareSettingsOpened: boolean = false;

    currentUserSubjectSubscription: Subscription;
    dataUpdateSubscription: Subscription;

    constructor(
        private channelHistoryService: ChannelHistoryService,
        public translation: TranslateService,
        private toastr: ToastrService,
        public responsive: ResponsiveService,
        private trendGraphAccessService: TrendGraphAccessSettingsService,
        public dialog: MatDialog,
        private authService: AuthService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.setChannels(this.view);
        this.resetSeriesVisibility();
        this.onDataUpdate = this.channelHistoryService.onHistoryUpdated();
        this.setLegendVisible(false);

        this.currentUserSubjectSubscription = this.authService.currentUser$.subscribe(async () => {
            this.shareSettingsOpened = false;
        });

        this.dataUpdateSubscription = this.onDataUpdate.subscribe((log: ChannelHistory) => {
            this.lastDataUpdate = log;
            if (!this.isInitComplete) {
                this.isInitComplete = true;
                this.cdr.markForCheck();
            }
            this.calculateNewHeights();

            const newHistoryMap = {};

            for (let i = 0; i < log.histories.length; i++) {
                const history = log.histories[i];
                newHistoryMap[history.fcuChannelId] = history;
            }

            this.historyMap = newHistoryMap;
        });

        this.updatePageAccessType();
        this.cdr.markForCheck();
    }

    ngAfterViewInit(): void {
        this.page.nativeElement.addEventListener('fullscreenchange', (event) => {
            this.calculateNewHeights();
            this.cdr.markForCheck();
        });

        window.addEventListener('resize', (event) => {
            this.calculateNewHeights();
            this.cdr.markForCheck();
        });

        this.page.nativeElement.addEventListener('fullscreenerror', (event) => {
            console.error(event);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.view && this.view) {
            this.stopRefreshing();

            this.setChannels(changes.view.currentValue);
            this.resetZoom();
            this.refreshColors();
            this.resetSeriesVisibility();
            this.selectChannel(null);
            this.setLegendVisible(true);
            this.updatePageAccessType();

            if (this.graphComponent) {
                this.graphComponent.resetVisibileChannels();
            }

            this.channelHistoryService.refresh();
            this.startRefresing();
            this.cdr.markForCheck()
        }
    }

    ngOnDestroy(): void {
        this.currentUserSubjectSubscription?.unsubscribe();
        this.dataUpdateSubscription?.unsubscribe();
        this.stopRefreshing();
        this.channelHistoryService.Cleanup();
    }

    handleGraphLoaded() {
        this.channelHistoryService.refresh();
    }

    onSaveCsvClicked() {
        this.saveAsCsv();
    }

    graphHasData() {
        const graphHistories = this.channelHistoryService.getGraphHistories();
        const activeChannels = Object.keys(graphHistories);

        let hasData = false;

        for (let i = 0; i < activeChannels.length; i++) {
            const channelHistoryEntry: ChannelHistoryEntry = graphHistories[activeChannels[i]];
            const channelLog: CircularBuffer = channelHistoryEntry.log;
            const channelHistories: any[] = channelLog.history;
            const onlyNullValues = channelHistories.every((element) => element === null);

            if (!onlyNullValues) {
                hasData = true;
                break;
            }
        }

        return hasData;
    }

    saveAsCsv() {
        if (!this.graphHasData()) {
            this.dialog.open(DescriptionDialogComponent, {
                data: {
                    title: 'trend-graph.save-csv.dialog.title',
                    description: 'trend-graph.save-csv.dialog.description',
                    closeButtonText: 'trend-graph.save-csv.dialog.close',
                    extensionButtonLeft: true,
                },
            });
            return;
        }
        const csv = this.graphComponent.getCsvData();

        const csvString = csv.map((v) => v.join(',')).join('\n');

        const saveTime = new Date();
        const filename =
            saveTime.getFullYear() +
            '-' +
            saveTime.getMonth() +
            '-' +
            saveTime.getDay() +
            '_' +
            saveTime.getHours() +
            ':' +
            saveTime.getMinutes() +
            ':' +
            saveTime.getSeconds() +
            ':' +
            saveTime.getMilliseconds() +
            '.csv';

        saveAs(new Blob([csvString]), filename);
    }

    public getLatestValue(fcuChannelId: number) {
        const history = this.historyMap[fcuChannelId];
        if (history) {
            const latestValue = history.log.getValue(history.log.length - 1);

            return ChannelFns.transformValue(this.channelLookup[history.fcuChannelId], latestValue);
        }
    }

    public async setChannels(newView: TrendGraphView) {
        this.channelHistoryService.setWatchedChannels(newView.channels.map((v) => v.fcuChannelId));
    }

    public refreshColors() {
        this.colors = {};
        for (let i = 0; i < this.view.channels.length; i++) {
            this.colors[this.view.channels[i].fcuChannelId] = this.view.channels[i].color;
        }
    }

    selectChannel(fcuChannelId: number) {
        this.selectedChannel = fcuChannelId;

        if (this.selectedChannel == null) {
            this.returnToDefaultVisibility();
        } else {
            this.showOnly(this.selectedChannel);
        }
    }

    isSelected(channel: ChannelViewOption) {
        return channel.fcuChannelId == this.selectedChannel;
    }

    isVisible(channel: ChannelViewOption) {
        return this.visibilityMap[channel.fcuChannelId];
    }

    visibilityToggleClicked(event: MouseEvent, channel: ChannelViewOption) {
        event.stopPropagation();
        this.toggleVisibility(channel.fcuChannelId);
    }

    toggleVisibility(channelNo: number) {
        this.visibilityMap[channelNo] = !this.visibilityMap[channelNo];
        const updateMap = {};
        updateMap[channelNo] = this.visibilityMap[channelNo];
        this.updateGraphVisibility(updateMap);
    }

    showOnly(visibleChannelNo: number) {
        const exclusiveVisibilityMap = {};

        for (let i = 0; i < this.view.channels.length; i++) {
            const channel = this.view.channels[i];
            exclusiveVisibilityMap[channel.fcuChannelId] =
                channel.fcuChannelId == visibleChannelNo && this.isVisible(channel);
        }

        this.graphComponent.setChannelVisibility(exclusiveVisibilityMap);
    }

    returnToDefaultVisibility() {
        this.updateGraphVisibility(this.visibilityMap);
    }

    resetSeriesVisibility() {
        this.visibilityMap = {};

        for (let i = 0; i < this.view.channels.length; i++) {
            this.visibilityMap[this.view.channels[i].fcuChannelId] = true;
        }

        this.updateGraphVisibility(this.visibilityMap);
    }

    updateGraphVisibility(visibilityMap) {
        if (this.graphComponent) this.graphComponent.setChannelVisibility(visibilityMap);
    }

    isAtDefaultZoom() {
        return (
            this.dateRange.startIndex == this.MIN_DATE_INDEX &&
            this.dateRange.endIndex == this.MAX_DATE_INDEX &&
            this.verticalViewRange.start == this.MIN_GRAPH_VALUE &&
            this.verticalViewRange.end == this.MAX_GRAPH_VALUE
        );
    }

    resetZoom() {
        this.dateRange.startIndex = this.MIN_DATE_INDEX;
        this.dateRange.endIndex = this.MAX_DATE_INDEX;

        this.verticalViewRange.start = this.MIN_GRAPH_VALUE;
        this.verticalViewRange.end = this.MAX_GRAPH_VALUE;
    }

    startRefresing() {
        if (this.updateInterval !== null) return;

        this.updateInterval = setInterval(() => {
            this.channelHistoryService.refresh();
        }, 1000);
    }

    stopRefreshing() {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
    }

    fullscreen() {
        if (!document.fullscreenEnabled) {
            //TODO add an error message
        }

        this.page.nativeElement.requestFullscreen();
    }

    exitFullscreen() {
        document.exitFullscreen();
    }

    isFullscreen() {
        return document.fullscreenElement != null;
    }

    setLegendVisible(visible: boolean) {
        this.isLegendVisible_ = visible;
    }

    isLegendVisible() {
        return this.isLegendVisible_;
    }

    getLegendLabel(channel: ChannelViewOption) {
        return this.channelLookup[channel.fcuChannelId]
            ? this.channelLookup[channel.fcuChannelId].opsData.name
            : channel.fcuChannelId;
    }

    getChannelColor(channel: ChannelViewOption) {
        return this.colors[channel.fcuChannelId];
    }

    saveScreenshot() {
        this.graphComponent.getAsImage().then((data) => {
            // saveAs(data, filename);
            navigator.clipboard
                .write([
                    new ClipboardItem({
                        [data.type]: data,
                    }),
                ])
                .then(() => {
                    this.translation.get('general.copied-to-clipboard').subscribe((v) => {
                        this.toastr.success(v);
                    });
                });
        });
    }

    emitEdit() {
        this.onEditView.emit(this.view);
    }

    emitDelete() {
        this.onDeleteView.emit(this.view.id);
    }

    calculateNewHeights() {
        if (this.isSmallScreenDesign()) {
            this.graphMinHeight = '25vh';
        } else {
            this.graphMinHeight = this.DEFAULT_MIN_HEIGHT;
        }

        const graphElementHeight = this.graphElement.nativeElement.offsetHeight + 5;
        this.verticalSliderHeight = graphElementHeight;
    }

    public isSmallScreenDesign() {
        return (
            this.responsive.isSmall() ||
            this.responsive.isXSmall() ||
            this.responsive.isHandset() ||
            this.responsive.isTabletPortrait() ||
            this.responsive.isWebPortrait()
        );
    }

    async updateUsersAccess(updatedUsersAccess) {
        await this.trendGraphAccessService.update(updatedUsersAccess);
    }

    async updatePageAccess(pageAccess) {
        await this.trendGraphAccessService.updatePageAccess(pageAccess, this.view.id);
    }

    updatePageAccessType() {
        if (this.view.publicAccess) {
            this.pageAccessType = PageAccessType.Public;
            this.pageAccessIcon = 'lock-open';
        } else {
            this.pageAccessType = PageAccessType.Private;
            this.pageAccessIcon = 'lock-closed';
        }
    }

    changeShareSettings(e: MouseEvent) {
        this.shareSettingsOpened = !this.shareSettingsOpened;
        e.stopPropagation();
    }
}
